import React, { useState, useEffect } from 'react';
import { Camera, Pencil, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import BarcodeScannerModal from './BarcodeScannerModal';
import { confirmDialog } from "../ui/confirm-dialog";

const InventoryDemoUnit = ({ axiosInstance, selectedWarehouse, warehouses = [] }) => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedCode, setScannedCode] = useState("");
    const [scannerMode, setScannerMode] = useState(""); // 'main_search'
    
    // State for Stock Demo Unit Management
    const [demoUnits, setDemoUnits] = useState([]);
    const [selectedDemoUnit, setSelectedDemoUnit] = useState(null);
    const [isEditDemoUnitOpen, setIsEditDemoUnitOpen] = useState(false);

    // State for Brand Management
    const [brands, setBrands] = useState([]);

    // State for Demo Unit Details Modal
    const [isDemoUnitDetailsOpen, setIsDemoUnitDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelDemoUnits, setModelDemoUnits] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchDemoUnits();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchDemoUnits = async () => {
        try {
            const response = await axiosInstance.get('/stock/demo-units');
            setDemoUnits(response.data);
        } catch (error) {
            console.error("Error fetching demo units:", error);
            toast.error("Failed to fetch demo units");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/demo-units/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleScanSuccess = (code) => {
        setScannedCode(code);
        if (scannerMode === 'main_search') {
            setSearchFilter(code);
        }
        setIsScannerOpen(false);
        setScannerMode("");
    };

    const openScanner = (mode) => {
        setScannerMode(mode);
        setIsScannerOpen(true);
    };

    const handleRowClick = (aggregatedDemoUnit) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const relatedDemoUnits = filteredDemoUnits.filter(d => 
            normalize(d.model) === normalize(aggregatedDemoUnit.model) && 
            normalize(d.brand) === normalize(aggregatedDemoUnit.brand) &&
            normalize(d.itemDescription) === normalize(aggregatedDemoUnit.itemDescription) &&
            normalize(d.itemCode) === normalize(aggregatedDemoUnit.itemCode) &&
            (d.warehouse_id || 'null') === (aggregatedDemoUnit.warehouse_id || 'null')
        );
        setSelectedModel(aggregatedDemoUnit);
        setModelDemoUnits(relatedDemoUnits);
        setIsDemoUnitDetailsOpen(true);
    };

    const handleEditDemoUnit = (demoUnit) => {
        setSelectedDemoUnit(demoUnit);
        setIsEditDemoUnitOpen(true);
    };

    const handleDeleteDemoUnit = async (demoUnitId) => {
        const confirmed = await confirmDialog({
            title: "Delete Demo Unit",
            description: "Are you sure you want to delete this demo unit?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/demo-units/${demoUnitId}`);
            toast.success("Demo unit deleted successfully");
            
            // Refresh main list
            await fetchDemoUnits();
            
            // Update modal list
            setModelDemoUnits(prev => prev.filter(d => d.id !== demoUnitId));
            
            // If no demo units left for this model, close modal
            if (modelDemoUnits.length <= 1) {
                setIsDemoUnitDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting demo unit:", error);
            toast.error("Failed to delete demo unit");
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedDemoUnit(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdateDemoUnit = async () => {
        if (!selectedDemoUnit) return;
        
        try {
            const payload = {
                brand: selectedDemoUnit.brand,
                model: selectedDemoUnit.model,
                itemDescription: selectedDemoUnit.itemDescription,
                itemCode: selectedDemoUnit.itemCode,
                serialNumber: selectedDemoUnit.serialNumber,
                quantity: parseInt(selectedDemoUnit.quantity) || 0,
                area: selectedDemoUnit.area || '',
                warehouse_id: selectedDemoUnit.warehouse_id || null
            };

            await axiosInstance.put(`/stock/demo-units/${selectedDemoUnit.id}`, payload);
            toast.success("Demo unit updated successfully");
            setIsEditDemoUnitOpen(false);
            setSelectedDemoUnit(null);
            
            // Refresh main list
            await fetchDemoUnits();
            
            // Update local state for details modal if it's open
            if (isDemoUnitDetailsOpen && selectedModel) {
                const response = await axiosInstance.get('/stock/demo-units');
                const allDemoUnits = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelDemoUnits = allDemoUnits.filter(d => 
                    normalize(d.model) === normalize(selectedModel.model) && 
                    normalize(d.brand) === normalize(selectedModel.brand) &&
                    normalize(d.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(d.itemCode) === normalize(selectedModel.itemCode) &&
                    (d.warehouse_id || 'null') === (selectedModel.warehouse_id || 'null')
                );
                setModelDemoUnits(updatedModelDemoUnits);
            }
        } catch (error) {
            console.error("Error updating demo unit:", error);
            toast.error("Failed to update demo unit");
        }
    };

    // Filter and Sort Demo Units
    const filteredDemoUnits = demoUnits.filter(demoUnit => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? demoUnit.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (demoUnit.itemCode?.toLowerCase().includes(searchLower) || 
             demoUnit.serialNumber?.toLowerCase().includes(searchLower) ||
             demoUnit.model?.toLowerCase().includes(searchLower) ||
             demoUnit.itemDescription?.toLowerCase().includes(searchLower));
        
        // Warehouse Filter
        const matchesWarehouse = !selectedWarehouse || demoUnit.warehouse_id === selectedWarehouse.id;

        return matchesBrand && matchesSearch && matchesWarehouse;
    });

    const getWarehouseName = (id) => {
        if (!id) return "Unassigned";
        const w = warehouses.find(w => w.id === id);
        return w ? w.name : "Unknown";
    };

    // Aggregate Demo Units by Brand + Model + Description + ItemCode + Warehouse
    const aggregatedDemoUnits = Object.values(filteredDemoUnits.reduce((acc, demoUnit) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        const key = `${normalize(demoUnit.brand)}-${normalize(demoUnit.model)}-${normalize(demoUnit.itemDescription)}-${normalize(demoUnit.itemCode)}-${demoUnit.warehouse_id || 'null'}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...demoUnit,
                quantity: 0,
                count: 0
            };
        }
        acc[key].quantity += (demoUnit.quantity || 0); 
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedDemoUnits = aggregatedDemoUnits.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.quantity - a.quantity;
            case "lowest_qty":
                return a.quantity - b.quantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    const totalQuantity = sortedAggregatedDemoUnits.reduce((sum, demoUnit) => sum + demoUnit.count, 0);

    return (
        <div className="h-full flex flex-col space-y-4">
            {/* Top Controls Bar */}
            <div className="bg-white p-4 rounded-lg shadow flex flex-wrap gap-4 items-end">
                <div className="w-[200px] space-y-2">
                    <Label>Brand</Label>
                    <Select value={brandFilter} onValueChange={setBrandFilter}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select Brand" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Brands</SelectItem>
                            {brands.map(brand => (
                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex-1 space-y-2 min-w-[200px]">
                    <Label>Search</Label>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Search Model, Description, Item Code, or Serial..."
                            value={searchFilter}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            className="flex-1"
                        />
                        <Button 
                            variant="outline" 
                            size="icon" 
                            onClick={() => openScanner('main_search')}
                        >
                            <Camera className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                <div className="w-[200px] space-y-2">
                    <Label>Sort By</Label>
                    <Select value={sortOption} onValueChange={setSortOption}>
                        <SelectTrigger>
                            <SelectValue placeholder="Sort By" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                            <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                            <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stock List Table */}
            <div className="bg-white p-4 rounded-lg shadow flex-1 overflow-hidden flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-orange-600 ml-1">{sortedAggregatedDemoUnits.length}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-600 px-3 py-1.5 rounded-md border border-gray-200">
                        Total: <span className="text-black font-bold ml-1">{totalQuantity}</span>
                    </div>
                </div>
                <div className="border rounded-md flex-1 overflow-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="font-bold">Warehouse</TableHead>
                                <TableHead className="font-bold">Brand</TableHead>
                                <TableHead>Model</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Item Code</TableHead>
                                <TableHead>Area</TableHead>
                                <TableHead>Quantity</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {sortedAggregatedDemoUnits.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} className="text-center py-4 text-gray-500">
                                        No demo units found.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedAggregatedDemoUnits.map((demoUnit, index) => (
                                    <TableRow 
                                        key={`${demoUnit.brand}-${demoUnit.model}-${index}`} 
                                        className="hover:bg-green-100 cursor-pointer transition-colors"
                                        onClick={() => handleRowClick(demoUnit)}
                                    >
                                        <TableCell>{getWarehouseName(demoUnit.warehouse_id)}</TableCell>
                                        <TableCell className="font-bold">{demoUnit.brand}</TableCell>
                                        <TableCell>{demoUnit.model}</TableCell>
                                        <TableCell>{demoUnit.itemDescription}</TableCell>
                                        <TableCell>{demoUnit.itemCode}</TableCell>
                                        <TableCell>{demoUnit.area}</TableCell>
                                        <TableCell>{demoUnit.count}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Demo Unit Details Modal */}
            <Dialog open={isDemoUnitDetailsOpen} onOpenChange={setIsDemoUnitDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Demo Unit Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Total Quantity: {modelDemoUnits.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                {isSuperAdmin && <TableHead>Added By</TableHead>}
                                {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                <TableHead>Warehouse</TableHead>
                                <TableHead>Brand</TableHead>
                                <TableHead>Model</TableHead>
                                <TableHead>Item Description</TableHead>
                                <TableHead>Item Code</TableHead>
                                <TableHead>Area</TableHead>
                                <TableHead>Serial Number</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelDemoUnits.map((demoUnit) => (
                                    <TableRow key={demoUnit.id}>
                                        {isSuperAdmin && <TableCell>{demoUnit.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(demoUnit.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{getWarehouseName(demoUnit.warehouse_id)}</TableCell>
                                        <TableCell>{demoUnit.brand}</TableCell>
                                        <TableCell>{demoUnit.model}</TableCell>
                                        <TableCell>{demoUnit.itemDescription}</TableCell>
                                        <TableCell>{demoUnit.itemCode}</TableCell>
                                        <TableCell>{demoUnit.area}</TableCell>
                                        <TableCell className="font-medium">{demoUnit.serialNumber}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditDemoUnit(demoUnit)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                {isSuperAdmin && (
                                                    <Button 
                                                        variant="ghost" 
                                                        size="icon"
                                                        onClick={() => handleDeleteDemoUnit(demoUnit.id)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDemoUnitDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Demo Unit Modal */}
            <Dialog open={isEditDemoUnitOpen} onOpenChange={setIsEditDemoUnitOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Demo Unit</DialogTitle>
                        <DialogDescription>
                            Update the details of the demo unit stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedDemoUnit && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedDemoUnit.brand} 
                                        onValueChange={(value) => setSelectedDemoUnit(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedDemoUnit.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedDemoUnit.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedDemoUnit.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-area" className="text-right">
                                    Area
                                </Label>
                                <Input
                                    id="edit-area"
                                    name="area"
                                    value={selectedDemoUnit.area || ''}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-warehouse" className="text-right">
                                    Warehouse
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedDemoUnit.warehouse_id || "unassigned"} 
                                        onValueChange={(value) => setSelectedDemoUnit(prev => ({ ...prev, warehouse_id: value === "unassigned" ? null : value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Warehouse" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="unassigned">Unassigned</SelectItem>
                                            {warehouses.map(warehouse => (
                                                <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-serialNumber" className="text-right">
                                    Serial No.
                                </Label>
                                <div className="col-span-3 flex gap-2">
                                    <Input
                                        id="edit-serialNumber"
                                        name="serialNumber"
                                        value={selectedDemoUnit.serialNumber}
                                        onChange={handleEditInputChange}
                                        className="flex-1"
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline" 
                                        size="icon" 
                                        onClick={() => openScanner('edit_serial')}
                                    >
                                        <Camera className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedDemoUnit.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateDemoUnit}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <BarcodeScannerModal 
                isOpen={isScannerOpen} 
                onClose={() => setIsScannerOpen(false)} 
                onScanSuccess={handleScanSuccess} 
            />
        </div>
    );
};

export default InventoryDemoUnit;
